import {initCenteringGuidelines} from '@Libraries/centering-guide-lines-library';
import {getScaleForFitToContainer} from '@Utils/math.util';
import {loadImageAsync} from '@Utils/image.util';
import type {MaskingItemObject} from '@PosterWhiteboard/classes/item-masking.class';
import {initDefaultFabricControls} from '@PosterWhiteboard/poster/poster-item-controls';
import type {CropData} from '@PosterWhiteboard/classes/item-crop.class';
import type {NudgeValues} from '@Libraries/poster-item-keyboard-movement';
import {initPosterItemKeyboardMovement} from '@Libraries/poster-item-keyboard-movement';
import type {KeyboardShortcutScope} from '@Libraries/keyboard-shortcuts';
import hotkeys from 'hotkeys-js';
import type {CanvasEvents} from '@postermywall/fabricjs-2';
import {FabricImage, Canvas} from '@postermywall/fabricjs-2';

export interface MaskOptsInterface {
  imageErrorHandler?(): void;

  initialMaskingData?: MaskingItemObject;
  cropData?: CropData;
}

export abstract class Mask {
  public id = '';
  public scale = 1;
  public url: string;
  public imageWidth = 0;
  public imageHeight = 0;
  protected abstract keyboardShortcutScope: KeyboardShortcutScope;

  protected loader!: HTMLImageElement;
  protected canvas!: Canvas;

  protected imageErrorHandler?(): void;

  protected initialMaskingData?: MaskingItemObject;
  protected cropData?: CropData;
  protected readonly parentSelector;

  private lastValidItemLeft = 0;
  private lastValidItemTop = 0;
  private lastKeyUpAt: Date = new Date();
  private lastSetTimeOut: Date = new Date();
  private objectTranslateDelta = 1;
  protected longKeyPress = false;

  public constructor(id: string, url: string, parentSelector: string, opts: MaskOptsInterface = {}) {
    if (!id) {
      throw new Error('Specify a valid id for mask');
    }

    this.id = id;
    this.imageErrorHandler = opts.imageErrorHandler?.bind(this);
    this.initialMaskingData = opts.initialMaskingData;
    this.cropData = opts.cropData;
    this.url = url;
    this.parentSelector = parentSelector;
  }

  public async init(): Promise<void> {
    this.loader = await loadImageAsync(this.url);
    initDefaultFabricControls(true);
    await this.initCanvas();
    this.bindKeyboardShortcuts();
  }

  public async dispose(): Promise<void> {
    this.unbindKeyboardShortcuts();
    await this.canvas.dispose();
  }

  private async initCanvas(): Promise<void> {
    const c = document.getElementById(this.id) as HTMLCanvasElement;

    c.width = this.cropData ? this.cropData.width : this.loader.width;
    c.height = this.cropData ? this.cropData.height : this.loader.height;
    this.imageWidth = c.width;
    this.imageHeight = c.height;

    this.canvas = new Canvas(c, {
      hoverCursor: 'pointer',
      selection: true,
      controlsAboveOverlay: true,
      center: true,
      allowTouchScrolling: true,
    });

    initCenteringGuidelines(this.canvas);
    this.canvas.on('object:moving', this.onObjectMoved.bind(this));

    this.canvas.backgroundImage = new FabricImage(this.loader, {
      width: this.cropData ? this.cropData.width : this.canvas.width,
      height: this.cropData ? this.cropData.height : this.canvas.height,
      cropX: this.cropData ? this.cropData.x : 0,
      cropY: this.cropData ? this.cropData.y : 0,
      originX: 'left',
      originY: 'top',
      opacity: 1,
    });
    this.canvas.requestRenderAll();
    this.setInitialZoom();
  }

  public onObjectMoved(e: CanvasEvents['object:moving']): void {
    const obj = e.target;
    if (!obj?.canvas) {
      return;
    }

    let validLeft = false;
    let validTop = false;
    obj.setCoords();

    if (!(obj.getBoundingRect().top + obj.getBoundingRect().height < 0 || obj.getBoundingRect().top > obj.canvas.height)) {
      this.lastValidItemTop = obj.top;
      validTop = true;
    }

    if (!(obj.getBoundingRect().left + obj.getBoundingRect().width < 0 || obj.getBoundingRect().left > obj.canvas.width)) {
      this.lastValidItemLeft = obj.left;
      validLeft = true;
    }

    if (!validLeft || !validTop) {
      obj.left = this.lastValidItemLeft;
      obj.top = this.lastValidItemTop;
      obj.setCoords();
      this.invalidate();
    }
  }

  protected getScaleX(): number {
    return this.initialMaskingData ? this.imageWidth / this.initialMaskingData.imageWidth : 1;
  }

  protected getScaleY(): number {
    return this.initialMaskingData ? this.imageHeight / this.initialMaskingData.imageHeight : 1;
  }

  public discardActiveObject(): void {
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
  }

  private bindKeyboardShortcuts(): void {
    initPosterItemKeyboardMovement(this.keyboardShortcutScope, this.onNudgeItem.bind(this), this.onNudgeItemEnd.bind(this));
    hotkeys.setScope(this.keyboardShortcutScope);
  }

  private unbindKeyboardShortcuts(): void {
    hotkeys.deleteScope(this.keyboardShortcutScope);
  }

  private onNudgeItem(values: NudgeValues): void {
    const activeObject = this.canvas.getActiveObjects();
    if (!activeObject[0]) {
      return;
    }

    activeObject[0].set({
      left: activeObject[0].left + values.x,
      top: activeObject[0].top + values.y,
    });

    this.canvas.requestRenderAll();
    this.onSelectionNudged();
  }

  protected onSelectionNudged(): void {}

  protected onNudgeItemEnd(): void {}

  public setInitialZoom(): void {
    const htmlElement = document.querySelector(`#${this.id}`)?.closest(this.parentSelector);
    if (htmlElement instanceof HTMLElement) {
      const availableWidth = htmlElement.offsetWidth - 20;
      const availableHeight = htmlElement.offsetHeight - 20;

      this.setZoom(getScaleForFitToContainer(availableWidth, availableHeight, this.imageWidth, this.imageHeight));
    }
  }

  public setZoom(value: number): void {
    // calculate multiplier for scaling the points.
    const multiplier = value / this.scale;
    this.scale = value;
    this.canvas.setDimensions({
      width: this.canvas.width * multiplier,
      height: this.canvas.height * multiplier,
    });

    // setting zoom
    this.canvas.setZoom(this.scale);
    this.invalidate();
  }

  // public onImageLoadError_(): void {
  //   if (this.opts.imageErrorHandler) {
  //     this.opts.imageErrorHandler(this.loader.src);
  //   }
  // }

  protected invalidate(): void {
    this.canvas.requestRenderAll();
  }
}
